import { Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDate, inr } from "@/lib/format";
import {
  chargeFor,
  campaignContractTotal,
  defaultChargeForHoarding,
  latestBusinessMargins,
  marginProfit,
  updateCharge,
} from "./utils";

function RequiredLabel({ children }) {
  return (
    <Label className="text-xs font-semibold text-slate-600">
      {children} <span className="text-blue-500">*</span>
    </Label>
  );
}

export function CampaignDialog({
  open, onOpenChange, editing,
  form, setForm,
  clients,
  allHoardings, pickableHoardings, hoardingSearch, setHoardingSearch,
  canAssignHoardings, canChangeDates, canEditAmounts, canViewRevenue,
  businessMargins = [],
  accountingSettings,
  bookingConflicts = [],
  originalCampaign = null,
  onSave,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const originalStart = (originalCampaign?.start_date || editing?.start_date || "").slice(0, 10);
  const originalEnd = (originalCampaign?.end_date || editing?.end_date || "").slice(0, 10);
  const isEditing = Boolean(editing || originalCampaign);
  const startDateLocked = isEditing && originalStart && originalStart < today;
  const endDateLocked = isEditing && originalEnd && originalEnd < today;
  const contractTotal = campaignContractTotal(form, allHoardings);
  const gstTreatment = form.gst?.gst_treatment || "cgst_sgst";

  function pickClient(id) {
    const cl = clients.find((c) => c.id === id);
    setForm((p) => ({ ...p, client_id: id, client_name: cl?.company || "", client_mobile: cl?.mobile || "" }));
  }

  function toggleHoarding(hId, checked) {
    setForm((p) => {
      const ids = checked ? [...p.hoarding_ids, hId] : p.hoarding_ids.filter((x) => x !== hId);
      const amounts = { ...p.contract_amounts };
      if (checked) {
        const h = allHoardings.find((item) => item.id === hId);
        const effectiveMargins = latestBusinessMargins(businessMargins, p.start_date);
        amounts[hId] = defaultChargeForHoarding(h, effectiveMargins, amounts[hId]);
      }
      if (!checked) delete amounts[hId];
      return { ...p, hoarding_ids: ids, contract_amounts: amounts };
    });
  }

  function updateHoardingCharge(hId, field, value) {
    setForm((p) => ({ ...p, contract_amounts: updateCharge(p.contract_amounts, hId, field, value) }));
  }

  function updateGst(field, value) {
    setForm((p) => {
      const gst = { ...(p.gst || {}) };
      if (field === "gst_percentage") {
        const pct = Number(value || 0);
        return {
          ...p,
          gst: {
            ...gst,
            gst_percentage: value,
            ...(gst.gst_treatment === "igst"
              ? { igst_percentage: value }
              : { cgst_percentage: pct / 2, sgst_percentage: pct / 2 }),
          },
        };
      }
      if (field === "gst_treatment") {
        const pct = Number(gst.gst_percentage || accountingSettings?.default_gst_percentage || 18);
        return {
          ...p,
          gst: {
            ...gst,
            gst_treatment: value,
            ...(value === "igst"
              ? { igst_percentage: gst.igst_percentage || pct }
              : { cgst_percentage: gst.cgst_percentage || pct / 2, sgst_percentage: gst.sgst_percentage || pct / 2 }),
          },
        };
      }
      if (field === "igst_percentage") return { ...p, gst: { ...gst, igst_percentage: value, gst_percentage: value } };
      if (field === "cgst_percentage" || field === "sgst_percentage") {
        const next = { ...gst, [field]: value };
        next.gst_percentage = Number(next.cgst_percentage || 0) + Number(next.sgst_percentage || 0);
        return { ...p, gst: next };
      }
      return { ...p, gst: { ...gst, [field]: value } };
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto hx-campaign-dialog-compact" data-testid="campaign-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Campaign" : "New Campaign"}</DialogTitle>
          <DialogDescription>Assign hoardings to a client. Booking statuses update automatically.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
          <div className="space-y-1 sm:col-span-2">
            <RequiredLabel>Campaign Name</RequiredLabel>
            <Input data-testid="camp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Summer 2026 Launch" />
          </div>

          <div className="space-y-1">
            <RequiredLabel>Client</RequiredLabel>
            <Select value={form.client_id || "none"} onValueChange={(v) => v === "none" ? setForm({ ...form, client_id: null, client_name: "" }) : pickClient(v)}>
              <SelectTrigger data-testid="camp-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {clients.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.company}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <RequiredLabel>Start Date</RequiredLabel>
            <Input data-testid="camp-start" type="date" value={form.start_date} disabled={(!!editing && !canChangeDates) || startDateLocked} min={startDateLocked ? originalStart : undefined} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            {startDateLocked && <p className="text-[11px] font-semibold text-amber-700">Started campaigns cannot have their start date changed.</p>}
          </div>

          <div className="space-y-1">
            <RequiredLabel>End Date</RequiredLabel>
            <Input data-testid="camp-end" type="date" value={form.end_date || ""} min={isEditing && originalEnd >= today ? today : undefined} disabled={form.is_open_ended || (!!editing && !canChangeDates) || endDateLocked} onChange={(e) => setForm({ ...form, end_date: e.target.value, end_date_status: "confirmed" })} />
            {endDateLocked && <p className="text-[11px] font-semibold text-amber-700">Ended campaigns cannot have their end date changed.</p>}
            {isEditing && originalEnd >= today && !endDateLocked && <p className="text-[11px] font-semibold text-slate-500">End date can be moved to today or a future date only.</p>}
          </div>

          <div className="space-y-3 sm:col-span-2 rounded-lg border border-amber-200/70 bg-amber-50/60 p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Checkbox
                checked={!!form.is_open_ended}
                disabled={!!editing && !canChangeDates}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  is_open_ended: !!checked,
                  end_date: checked ? "" : form.end_date,
                  end_date_status: checked ? "unknown" : "confirmed",
                })}
              />
              End date not known yet
            </label>
            {form.is_open_ended && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Expected / Tentative End Date</span>
                  <Input type="date" value={form.expected_end_date || ""} onChange={(e) => setForm({ ...form, expected_end_date: e.target.value, end_date_status: e.target.value ? "tentative" : "unknown" })} />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">End Date Status</span>
                  <Select value={form.end_date_status || "unknown"} onValueChange={(v) => setForm({ ...form, end_date_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Unknown</SelectItem>
                      <SelectItem value="tentative">Tentative</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>
            )}
          </div>

          {canViewRevenue && (
            <div className="space-y-3 sm:col-span-2 rounded-lg border p-3" style={{ borderColor: "var(--hx-border)", background: "var(--hx-bg)" }}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">PO Value Status</span>
                  <Select value={form.po_value_status || "confirmed"} onValueChange={(v) => setForm({ ...form, po_value_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="estimated">Estimated</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Confirmed PO Value</span>
                  <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-900">
                    {inr(contractTotal)}
                  </div>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Payment Due Date</span>
                  <Input type="date" value={form.payment_due_date || ""} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} />
                </label>
              </div>
              {form.po_value_status !== "confirmed" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-slate-600">Estimated PO Value</span>
                    <Input type="number" min="0" value={form.estimated_po_value || ""} onChange={(e) => setForm({ ...form, estimated_po_value: e.target.value })} />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-slate-600">Estimated Monthly Charge</span>
                    <Input type="number" min="0" value={form.estimated_monthly_charge || ""} onChange={(e) => setForm({ ...form, estimated_monthly_charge: e.target.value })} />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-slate-600">Estimated Daily Charge</span>
                    <Input type="number" min="0" value={form.estimated_daily_charge || ""} onChange={(e) => setForm({ ...form, estimated_daily_charge: e.target.value })} />
                  </label>
                </div>
              )}
            </div>
          )}

          {canViewRevenue && (
            <div className="space-y-3 sm:col-span-2 rounded-lg border border-emerald-200/70 bg-emerald-50/60 p-3">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">GST Mode</span>
                  <Select value={form.gst?.gst_mode || accountingSettings?.default_gst_mode || "exclusive"} onValueChange={(v) => updateGst("gst_mode", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="exclusive">Exclusive</SelectItem><SelectItem value="inclusive">Inclusive</SelectItem></SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">GST %</span>
                  <Input type="number" min="0" value={form.gst?.gst_percentage ?? accountingSettings?.default_gst_percentage ?? 18} onChange={(e) => updateGst("gst_percentage", e.target.value)} />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Split</span>
                  <Select value={gstTreatment} onValueChange={(v) => updateGst("gst_treatment", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="cgst_sgst">CGST + SGST</SelectItem><SelectItem value="igst">IGST</SelectItem></SelectContent>
                  </Select>
                </label>
                {gstTreatment === "igst" ? (
                  <label className="space-y-1">
                    <span className="text-xs font-semibold text-slate-600">IGST %</span>
                    <Input type="number" min="0" value={form.gst?.igst_percentage ?? accountingSettings?.default_igst_percentage ?? 18} onChange={(e) => updateGst("igst_percentage", e.target.value)} />
                  </label>
                ) : (
                  <>
                    <label className="space-y-1">
                      <span className="text-xs font-semibold text-slate-600">CGST %</span>
                      <Input type="number" min="0" value={form.gst?.cgst_percentage ?? accountingSettings?.default_cgst_percentage ?? 9} onChange={(e) => updateGst("cgst_percentage", e.target.value)} />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs font-semibold text-slate-600">SGST %</span>
                      <Input type="number" min="0" value={form.gst?.sgst_percentage ?? accountingSettings?.default_sgst_percentage ?? 9} onChange={(e) => updateGst("sgst_percentage", e.target.value)} />
                    </label>
                  </>
                )}
                <label className="space-y-1">
                  <span className="text-xs font-semibold text-slate-600">Round Off</span>
                  <Select value={form.gst?.round_off_behavior || accountingSettings?.default_round_off || "none"} onValueChange={(v) => updateGst("round_off_behavior", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="nearest_rupee">Nearest rupee</SelectItem></SelectContent>
                  </Select>
                </label>
              </div>
            </div>
          )}

          {bookingConflicts.length > 0 && (
            <div className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
              <div className="text-sm font-semibold text-rose-800">Double Booking Warning</div>
              <div className="mt-1 text-xs text-rose-700">
                {bookingConflicts[0].hoarding_name} is already booked by {bookingConflicts[0].client_name || bookingConflicts[0].campaign_name} from {fmtDate(bookingConflicts[0].start_date)} to {fmtDate(bookingConflicts[0].end_date)}.
              </div>
              {bookingConflicts.length > 1 && (
                <div className="mt-2 space-y-1">
                  {bookingConflicts.slice(1, 4).map((conflict) => (
                    <div key={`${conflict.campaign_id}-${conflict.hoarding_id}`} className="text-xs text-rose-700">
                      {conflict.hoarding_name}: {conflict.client_name || conflict.campaign_name} ({fmtDate(conflict.start_date)} to {fmtDate(conflict.end_date)})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <RequiredLabel>Assign Hoardings ({form.hoarding_ids.length} selected)</RequiredLabel>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--hx-border)", background: "var(--hx-bg)" }}>
              <Search size={13} className="text-slate-400 flex-shrink-0" />
              <input value={hoardingSearch} onChange={(e) => setHoardingSearch(e.target.value)} placeholder="Search by hoarding name or ID…" className="bg-transparent outline-none text-sm w-full" />
            </div>
            <div className="rounded-lg border max-h-64 overflow-y-auto hx-scroll p-2" style={{ borderColor: "var(--hx-border)", background: "color-mix(in srgb, var(--hx-bg) 60%, var(--hx-surface))" }}>
              {pickableHoardings.length === 0 && (
                <div className="py-6 text-center text-slate-400 text-sm">No hoardings match.</div>
              )}
              {pickableHoardings.map((h) => {
                const checked = form.hoarding_ids.includes(h.id);
                const charge = chargeFor(form.contract_amounts[h.id]);
                const printingProfit = marginProfit(charge.printing, charge.printing_margin_type, charge.printing_margin_value);
                const installationProfit = marginProfit(charge.installation, charge.installation_margin_type, charge.installation_margin_value);
                return (
                  <div key={h.id} className={`rounded-lg mb-1 border ${checked ? "border-blue-200 bg-blue-50/60" : "border-transparent hover:bg-slate-100"}`}>
                    <div className="flex items-center justify-between px-3 py-2">
                      <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                        <Checkbox
                          checked={checked}
                          disabled={!!editing && !canAssignHoardings}
                          onCheckedChange={(c) => toggleHoarding(h.id, c)}
                          data-testid={`hoarding-pick-${h.id}`}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{h.name} <span className="text-xs text-slate-400 ml-1 font-mono">{h.hoarding_id}</span></div>
                          <div className="text-xs text-slate-500">{h.area}, {h.city} · {h.type}</div>
                        </div>
                      </label>
                      {!checked && canViewRevenue && (
                        <div className="font-mono text-xs text-slate-500 ml-3">{inr(h.monthly_price)}</div>
                      )}
                    </div>
                    {checked && canViewRevenue && (!editing || canEditAmounts) && (
                      <div className="px-3 pb-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 rounded-lg bg-white border border-blue-100 p-2">
                          <label className="block">
                            <span className="block text-[10px] font-semibold text-slate-500">Display / month</span>
                            <input
                              type="number"
                              min="0"
                              value={charge.display || ""}
                              onChange={(e) => updateHoardingCharge(h.id, "display", e.target.value)}
                              placeholder={String(Math.round(h.monthly_price || 0))}
                              className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
                            />
                          </label>
                          {[
                            ["printing", "Printing", "printing_margin_type", "printing_margin_value", h.default_printing_charge, printingProfit],
                            ["installation", "Installation", "installation_margin_type", "installation_margin_value", h.default_installation_charge, installationProfit],
                          ].map(([amountField, label, typeField, valueField, fallback, profit]) => (
                            <div key={amountField} className="space-y-1">
                              <label className="block">
                                <span className="block text-[10px] font-semibold text-slate-500">{label}</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={charge[amountField] || ""}
                                  onChange={(e) => updateHoardingCharge(h.id, amountField, e.target.value)}
                                  placeholder={String(Math.round(fallback || 0))}
                                  className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
                                />
                              </label>
                              <div className="grid grid-cols-[1fr_72px] gap-1">
                                <Select value={charge[typeField] || "none"} onValueChange={(v) => updateHoardingCharge(h.id, typeField, v === "none" ? "" : v)}>
                                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No margin</SelectItem>
                                    <SelectItem value="percent">%</SelectItem>
                                    <SelectItem value="amount">₹</SelectItem>
                                  </SelectContent>
                                </Select>
                                <input
                                  type="number"
                                  min="0"
                                  value={charge[valueField] || ""}
                                  onChange={(e) => updateHoardingCharge(h.id, valueField, e.target.value)}
                                  className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
                                />
                              </div>
                              <div className="text-[10px] text-emerald-700">Profit: {inr(profit)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {canViewRevenue && form.hoarding_ids.length > 0 && (
              <div className="text-xs text-slate-500">
                Contract total:{" "}
                <span className="font-semibold text-slate-800">
                  {inr(contractTotal)}
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="camp-save" onClick={onSave}>
            {editing ? "Update" : "Create campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
